import CryptoJS from "react-native-crypto-js";
import {Engine} from "platform/engine/Engine";
import {
    DoChangePasswordPayload,
    DoLogout,
    DoLogoutPayload,
    DoSwitchAccountPayload,
    HandleLoginResponsePayload,
    OnAccountChanged,
    OnLoggedOut,
    SetAuthStatus,
} from "core/redux/auth/AuthReduxActions";
import {SetClientPreference, SetPreferencesFetched} from "core/redux/settings/SettingsReduxActions";
import Platform from "platform/Platform";
import {AuthState} from "core/state/AuthState";
import {ServiceType} from "enum/ServiceType";
import Utils from "platform/util/Utils";
import {NXEnvironmentType} from "platform/protocol/enum/NXEnvironmentType";
import {StoreState} from "core/redux/StoreState";
import {LoginAccountInfo} from "protocol/auth/LoginAccountInfo";
import {LoginToAccountRequest} from "protocol/auth/LoginToAccountRequest";
import {LoginToAccountResponse} from "protocol/auth/LoginToAccountResponse";
import {HttpReject} from "platform/network/http/Http";
import {Xhr} from "platform/network/xhr/Xhr";
import {ServerType} from "platform/enum/ServerType";
import {CloseAllPopups, ShowPopup} from "platform/redux/popups/PopupsActions";
import {PopupActionType, PopupIconType, PopupType} from "platform/redux/popups/PopupsReduxState";
import {StorageKey} from "enum/StorageKey";
import {LogoutRequest} from "protocol/auth/LogoutRequest";
import {XhrUtil} from "core/util/XhrUtil";
import {AccountState} from "core/state/AccountState";
import {BadRequestResponse} from "protocol/BadRequestResponse";
import {HideLoader, InitChat, SetLoader, SetUrls} from "platform/redux/core/CoreActions";
import {SetAccountInfo} from "core/redux/account/AccountReduxActions";
import {LoginConfigurationRequest} from "protocol/config/LoginConfigurationRequest";
import {Configuration} from "core/configuration/ConfigManager";
import {AvailableLanguage, LoginConfigurationResponse} from "protocol/config/LoginConfigurationResponse";
import SessionInactivity from "core/util/SessionInactivity";
import {LangCode} from "platform/enum/LangCode";
import Translations from "platform/translation/Translations";
import {TranslationKey} from "enum/TranslationKey";
import {ChangePasswordRequest} from "protocol/account/ChangePasswordRequest";
import {ChangePasswordResponse} from "protocol/account/ChangePasswordResponse";
import {AddInboxMessage} from "core/redux/inbox/InboxReduxActions";
import InboxMessageType from "enum/InboxMessageType";
import Parameter from "platform/util/Parameter";
import InboxParameter from "enum/InboxParameter";
import {ClientPreference} from "protocol/account/ClientPreference";
import {SetSupportedLanguages} from "platform/redux/translation/TranslationActions";
import {LSKey} from "platform/storage/Storage";
import {LanguageUtil} from "platform/util/LanguageUtil";
import {PreferencesManager} from "core/engine/PreferencesManager";
import {LoaderType} from "platform/enum/LoaderType";
import {Preference} from "core/util/Preference";
import {SetTradingModal} from "core/redux/app/AppReduxActions";
import TradingModalType from "enum/TradingModalType";
import {UrlType} from "platform/enum/UrlType";
import {AppCues} from "core/appCues/AppCues";
import {Account, GetAfterLoginConfigurationResponse} from "protocol/account/GetAfterLoginConfigurationResponse";
import WebUtil from "platform/util/WebUtil";
import {EnvType} from "platform/enum/EnvType";
import {GetAccountStateResponse} from "protocol/account/GetAccountStateResponse";
import {RouteType} from "core/router/Routes";
import {ResponseAccount} from "protocol/auth/LoginResponse";
import {LogOffRequest} from "protocol/auth/LogOffRequest";

const Token: string = WebUtil.urlParam("token") || WebUtil.urlParam("tokenId");
const Salt: string = "aptchsy8665dsds45s";
const K: string = "__#%$*()";

export default class AuthEngine extends Engine {

    private static _instance: AuthEngine;
    private _initialized: boolean;
    private _ping: any;

    public static instance(): AuthEngine {
        return this._instance || (this._instance = new this());
    }

    public async setup(): Promise<void> {
        await super.setup();
        SessionInactivity.setThreshold(Platform.config<Configuration>().activeTraderIdleTimeBeforeLogoff);
        this.startPing();
    }

    public startPing = (): void => {
        if (Utils.isNull(this._ping)) {
            this._logger.debug("Start ping");
            this.fetchConfig();
            this._ping = setInterval(this.fetchConfig, 1000);
        }
    }

    private stopPing = (): void => {
        if (Utils.isNotNull(this._ping)) {
            clearInterval(this._ping);
            this._ping = null;
        }
    }

    private fetchConfig = (): void => {
        const request: LoginConfigurationRequest = {
            BrandId: Platform.config<Configuration>().brandId,
            LanguageCode: LanguageUtil.languageCode()
        };
        this.sendToLogin(request, "LoginConfiguration")
            .then(async (response: LoginConfigurationResponse) => {
                if (response.SuccessStatus) {
                    this.stopPing();
                    Platform.dispatch(SetAuthStatus({
                        authStatus: {connected: true}
                    }));
                    if (!this._initialized) {
                        this._initialized = true;
                        this._logger.debug("Received brand configuration. Success: " + response.SuccessStatus);
                        Platform.dispatch(SetUrls({
                            urls: [
                                {type: UrlType.NewLogin, url: response.NewLoginUrl}
                            ]
                        }));
                        if (Platform.environment().type() === EnvType.Web) {
                            await this.doGetAfterLoginConfiguration();
                        } else {
                            await this.tryNativeLogin();
                        }
                        if (Utils.isArrayNotEmpty(response.AvailableLanguageConfigurations)) {
                            const authState: AuthState = Platform.state(ServiceType.Auth);
                            const languages: LangCode[] = [];
                            response.AvailableLanguageConfigurations.forEach((langConfig: AvailableLanguage) => {
                                const langCode: LangCode = LangCode[langConfig.Code.toUpperCase()];
                                if (Utils.isNotNull(langCode) && Platform.config<Configuration>().supportedLanguages.indexOf(langCode) > -1) {
                                    authState.addAvailableLanguage(langCode, langConfig);
                                    languages.push(langCode);
                                }
                            });
                            Platform.dispatch(SetSupportedLanguages({languages}));
                        }
                        if (response.ChatUrl) {
                            Platform.dispatch(InitChat({scriptUrl: response.ChatUrl}));
                        } else {
                            this._logger.warn("Z-chat url not configured");
                        }
                    }
                } else {
                    this._logger.debug("Failed fetch config: " + response.LocalizedErrorMessage);
                }
            })
            .catch((e) => {
                this._logger.warn("Failed fetch configuration");
            });
    }

    public async doGetAfterLoginConfiguration(): Promise<void> {
        const accountState: AccountState = Platform.state(ServiceType.Account);
        const authState: AuthState = Platform.state(ServiceType.Auth);
        let token: string = Token;
        if (token) {
            Platform.storage().setItem(LSKey.Token, Token);
        } else {
            token = await Platform.storage().getItem(LSKey.Token);
        }
        if (Utils.isNotEmpty(token)) {
            authState.token = token;
            accountState.accountType = NXEnvironmentType.Live;
            const answer: [HttpReject, GetAfterLoginConfigurationResponse] = await Utils.to(this.sendToLoginSecure({}, "GetAfterLoginConfiguration"));
            if (answer[0]) {
                let response: BadRequestResponse;
                try {
                    response = JSON.parse(answer[0].response);
                } catch (e) {
                    this._logger.warn("Can't parse get account state failed response");
                }
                if (Utils.isNull(response) || response.IsSecurityException) {
                    this._logger.debug("Failed fetch after Login details.");
                    this.clear();
                }
            } else {
                const {UserId, UserName, Accounts} = answer[1];
                accountState.userId = UserId;
                if (Utils.isArrayNotEmpty(Accounts)) {
                    Accounts.forEach((ac: Account) => {
                        authState.addAccount({
                            ProfitTradingGroupId: ac.H,
                            BaseAssetId: ac.A,
                            PlatformType: ac.E,
                            EnvironmentType: ac.B,
                            Id: ac.C,
                            BaseAssetName: ac.BaseAssetName
                        });
                    });
                }
                const realAccount: LoginAccountInfo = authState.getAccount(NXEnvironmentType.Live);
                const demoAccount: LoginAccountInfo = authState.getAccount(NXEnvironmentType.Demo);
                const hasRealAccount: boolean = Utils.isNotNull(realAccount);
                const hasDemoAccount: boolean = Utils.isNotNull(demoAccount);
                const accountType: NXEnvironmentType = hasRealAccount ? NXEnvironmentType.Live : NXEnvironmentType.Demo;
                const account: LoginAccountInfo = authState.getAccount(accountType);
                this._logger.info("Login response for account: " + +accountType + " Has demo: " + hasDemoAccount + " Has real: " + hasRealAccount);
                accountState.accountType = accountType;
                accountState.accountId = account.Id;
                Platform.dispatch(SetAuthStatus({
                    authStatus: {
                        authenticated: true,
                        hasRealAccount,
                        hasDemoAccount,
                        accountType,
                    }
                }));
                Platform.dispatch(SetAccountInfo({
                    accountInfo: {
                        username: UserName,
                        Currency: account.BaseAssetName
                    }
                }));
                SessionInactivity.start();
                this.fetchPreferences(account).catch(() => {});
                AppCues.instance().Identify({
                    userId: UserId,
                    username: UserName
                });
            }
        } else {
            this._logger.debug("Token absent");
            this.clear();
        }
    }

    public async tryNativeLogin(): Promise<void> {
        const token: string = await Platform.storage().getItem(LSKey.Token);
        if (Utils.isNotEmpty(token)) {
            const sAccountType: string = await Platform.storage().getItem(StorageKey.AccountType);
            const accountType: NXEnvironmentType = parseInt(sAccountType, 10);
            const accountState: AccountState = Platform.state(ServiceType.Account);
            const authState: AuthState = Platform.state(ServiceType.Auth);
            accountState.accountType = accountType;
            authState.token = token;
            this._logger.debug("Check native login for account: " + accountType + " Has token: " + Utils.isNotEmpty(token));
            this.pingAccountService().then(async (response: GetAccountStateResponse) => {
                const k: string = await Platform.storage().getItem(K);
                if (Utils.isNotEmpty(k)) {
                    authState.password = CryptoJS.AES.decrypt(k, Salt).toString(CryptoJS.enc.Utf8);
                }
                this._logger.debug("Native Logged in with account type: " + accountType);
                const sUserId: string = await Platform.storage().getItem(StorageKey.UserId);
                accountState.accountId = response.AccountId;
                accountState.userId = parseInt(sUserId, 10);
                await authState.deserializeAccounts();
                const username: string = await Platform.storage().getItem(StorageKey.Username);
                Platform.dispatch(SetAuthStatus({
                    authStatus: {
                        authenticated: true,
                        accountType,
                        hasRealAccount: Utils.isNotNull(authState.getAccount(NXEnvironmentType.Live)),
                        hasDemoAccount: Utils.isNotNull(authState.getAccount(NXEnvironmentType.Demo))
                    }
                }));
                const account: LoginAccountInfo = authState.getAccount(accountType);
                Platform.dispatch(SetAccountInfo({
                    accountInfo: {
                        username,
                        Currency: account.BaseAssetName
                    }
                }));
                SessionInactivity.start();
                this.fetchPreferences(account).catch(() => {});
            }).catch((httpReject: HttpReject) => {
                let response: BadRequestResponse;
                try {
                    response = JSON.parse(httpReject.response);
                } catch (e) {
                    this._logger.warn("Can't parse get account state failed response");
                }
                if (Utils.isNull(response) || response.IsSecurityException) {
                    this._logger.debug("Failed Native Login.");
                    this.clear();
                }
            });
        } else {
            this._logger.debug("Token absent in LS");
            this.clear();
        }
    }

    private clear = (): void => {
        this._logger.debug("Clear");
        const accountState: AccountState = Platform.state(ServiceType.Account);
        const authState: AuthState = Platform.state(ServiceType.Auth);
        accountState.clear();
        authState.clear();
        Platform.storage().removeItem(LSKey.Token);
        Platform.storage().removeItem(StorageKey.Username);
        Platform.storage().removeItem(StorageKey.UserId);
        Platform.storage().removeItem(StorageKey.AccountType);
        Platform.storage().removeItem(StorageKey.Accounts);
        Platform.storage().removeItem(K);
        if (Platform.environment().type() === EnvType.Web) {
            const loginUrl: string = Platform.reduxState<StoreState>().core.urls.get(UrlType.NewLogin);
            Platform.environment().redirect(loginUrl);
        } else {
            Platform.router().navigate(RouteType.Login);
        }
    }

    private fetchPreferences = async (account: LoginAccountInfo): Promise<void> => {
        const authState: AuthState = Platform.state(ServiceType.Auth);
        const accountState: AccountState = Platform.state(ServiceType.Account);
        const serverType: ServerType = accountState.accountType === NXEnvironmentType.Live ? ServerType.TradingReal : ServerType.TradingDemo;
        const origin: string = Platform.config().servers[serverType];
        const preferences: ClientPreference[] = await PreferencesManager.FetchPreferences(origin, authState.token, accountState.accountId, accountState.accountType);
        if (Utils.isArrayNotEmpty(preferences)) {
            preferences.forEach((clientPreference: ClientPreference) => {
                if (StorageKey.hasKey(clientPreference.PreferenceKey)) {
                    const key: StorageKey = clientPreference.PreferenceKey as StorageKey;
                    const preference: Preference<any> = Preference.deserialize(key, clientPreference.PreferenceValue);
                    Platform.dispatch(SetClientPreference({
                        key, preference
                    }));
                }
            });
        }
        Platform.dispatch(SetPreferencesFetched({}));
        return Promise.resolve();
    }

    public handleLoginResponse = async (payload: HandleLoginResponsePayload): Promise<void> => {
        const {username, password, response} = payload;
        const {Accounts, UserId} = response;
        const accountState: AccountState = Platform.state(ServiceType.Account);
        const authState: AuthState = Platform.state(ServiceType.Auth);
        authState.password = password;
        authState.token = response.Token;
        accountState.userId = UserId;
        Platform.storage().setItem(K, CryptoJS.AES.encrypt(password, Salt).toString());
        Platform.storage().setItem(LSKey.Token, response.Token);
        Platform.storage().setItem(StorageKey.Username, username);
        Platform.storage().setItem(StorageKey.UserId, UserId.toString());
        if (Utils.isArrayNotEmpty(Accounts)) {
            Accounts.forEach((ac: ResponseAccount) => {
                authState.addAccount({
                    ProfitTradingGroupId: ac.Account.H,
                    BaseAssetId: ac.Account.A,
                    PlatformType: ac.Account.E,
                    EnvironmentType: ac.Account.B,
                    Id: ac.Account.C,
                    BaseAssetName: ac.BaseAssetName
                });
            });
            authState.serializeAccounts();
        }
        const realAccount: LoginAccountInfo = authState.getAccount(NXEnvironmentType.Live);
        const demoAccount: LoginAccountInfo = authState.getAccount(NXEnvironmentType.Demo);
        const hasRealAccount: boolean = Utils.isNotNull(realAccount);
        const hasDemoAccount: boolean = Utils.isNotNull(demoAccount);
        const accountType: NXEnvironmentType = hasRealAccount ? NXEnvironmentType.Live : NXEnvironmentType.Demo;
        const account: LoginAccountInfo = authState.getAccount(accountType);
        this._logger.info("Login response for account: " +  + accountType + " Has demo: " + hasDemoAccount + " Has real: " + hasRealAccount);
        Platform.dispatch(SetAuthStatus({
            authStatus: {
                hasRealAccount,
                hasDemoAccount,
                connected: true
            }
        }));
        Platform.dispatch(SetAccountInfo({
            accountInfo: {
                username,
                Currency: account.BaseAssetName
            }
        }));
        if (Utils.isEmpty(response.Token)) {
            const answer: [HttpReject, boolean] = await Utils.to(this.doLoginAccountRequest(accountType));
            if (answer[1]) {
                Platform.router().navigate(RouteType.AccountOverview);
                SessionInactivity.start();
                return Promise.resolve();
            } else {
                return Promise.reject();
            }
        } else {
            Platform.storage().setItem(StorageKey.AccountType, accountType.toString());
            accountState.accountId = account.Id;
            accountState.accountType = accountType;
            Platform.dispatch(SetAuthStatus({
                authStatus: {
                    authenticated: true,
                    accountType
                }
            }));
            Platform.router().navigate(RouteType.AccountOverview);
            SessionInactivity.start();
            this.fetchPreferences(account).catch(() => {});
            return Promise.resolve();
        }
    }

    public doLoginAccountRequest = async (accountType: NXEnvironmentType): Promise<boolean> => {
        const authState: AuthState = Platform.state(ServiceType.Auth);
        const accountState: AccountState = Platform.state(ServiceType.Account);
        const account: LoginAccountInfo = authState.getAccount(accountType);
        if (Utils.isNotNull(account)) {
            this._logger.debug("Login to account: " + NXEnvironmentType[accountType]);
            Platform.dispatch(SetAuthStatus({
                authStatus: {loginInProgress: true}
            }));
            const request: LoginToAccountRequest = {EnvironmentType: accountType};
            const answer: [HttpReject, LoginToAccountResponse] = await Utils.to(this.sendToLoginSecure(request, "AccountLogin"));
            Platform.dispatch(SetAuthStatus({
                authStatus: {loginInProgress: false}
            }));
            if (answer[0]) {
                this._logger.info("Failed to send login to account request. Status: " + answer[0].status);
                XhrUtil.notifyReject(answer[0]);
                return Promise.reject();
            } else {
                const response: LoginToAccountResponse = answer[1];
                if (response.Token) {
                    this._logger.info("Successful login to account: " + accountType);
                    authState.token = response.Token;
                    accountState.accountType = accountType;
                    accountState.accountId = account.Id;
                    Platform.storage().setItem(LSKey.Token, response.Token);
                    Platform.storage().setItem(StorageKey.AccountType, accountType.toString());
                    Platform.dispatch(SetAuthStatus({
                        authStatus: {
                            accountType,
                            authenticated: true
                        }
                    }));
                    Platform.dispatch(SetAccountInfo({
                        accountInfo: {
                            Currency: account.BaseAssetName
                        }
                    }));
                    this.fetchPreferences(account).catch(() => {
                    });
                    return Promise.resolve(true);
                } else {
                    this._logger.info("Failed login to account: " + accountType + " Error: " + response.ErrorMessage);
                    Platform.dispatch(ShowPopup({
                        popup: {
                            type: PopupType.ERROR,
                            message: {customValue: response.LocalizedErrorMessage || response.ErrorMessage},
                            actions: [{type: PopupActionType.OK}]
                        }
                    }));
                    return Promise.reject();
                }
            }
        } else {
            this._logger.warn("Can't find account by type: " + accountType);
            return Promise.reject();
        }
    }

    public doSwitchAccount = async (payload: DoSwitchAccountPayload) => {
        Platform.dispatch(SetLoader({loaderType: LoaderType.FullScreen}));
        const answer: [HttpReject, boolean] = await Utils.to(this.doLoginAccountRequest(payload.accountType));
        Platform.dispatch(HideLoader({}));
        if (answer[1]) {
            Platform.dispatch(OnAccountChanged({}));
            return Promise.resolve();
        } else {
            return Promise.reject();
        }
    }

    public doConfirmLogout = () => {
        Platform.dispatch(ShowPopup({
            popup: {
                type: PopupType.CONFIRM,
                icon: PopupIconType.CONFIRM,
                showClose: true,
                message: {
                    trKey: TranslationKey.logoutConfirmation,
                },
                actions: [{type: PopupActionType.CANCEL, text: {trKey: TranslationKey.yes}, action: () => {
                        Platform.dispatch(DoLogout({}));
                    }}, {type: PopupActionType.OK, text: {trKey: TranslationKey.no}}]
            }
        }));
    }

    public doLogout = (payload: DoLogoutPayload) => {
        const {authenticated} = Platform.reduxState<StoreState>().auth;
        if (authenticated) {
            const onResponse = () => {
                this._logger.debug("Logged out.");
                Platform.dispatch(HideLoader({}));
                Platform.dispatch(CloseAllPopups({}));
                Platform.dispatch(OnLoggedOut({}));
                SessionInactivity.stop();
                this.clear();
            };
            if (payload.skipNotifyServer) {
                onResponse();
            } else {
                const request: LogoutRequest = {BrandId: Platform.config<Configuration>().brandId};
                Platform.dispatch(SetLoader({loaderType: LoaderType.FullScreen}));
                this.sendToLoginSecure(request, "Logout")
                    .then(onResponse)
                    .catch(onResponse);
            }
        } else {
            Platform.dispatch(OnLoggedOut({}));
        }
    }

    public doLogOff = async () => {
        this._logger.debug("Logged off.");
        const authState: AuthState = Platform.state(ServiceType.Auth);
        const request: LogOffRequest = {
            participantToken: null,
            sessionId: authState.token
        };
        await XhrUtil.sendAuthenticated(request, "WebProfitSite/Dashboard/LogOff");
        Platform.dispatch(HideLoader({}));
        Platform.dispatch(CloseAllPopups({}));
        Platform.dispatch(ShowPopup({
            popup: {
                type: PopupType.INFO,
                icon: PopupIconType.INFO,
                showClose: true,
                message: {
                    trKey: TranslationKey.yourSessionExpired,
                },
                actions: [{type: PopupActionType.OK, action: () => {
                        Platform.dispatch(OnLoggedOut({}));
                        this.clear();
                    }}]
            }
        }));
    }

    public doChangePassword = async (payload: DoChangePasswordPayload) => {
        if (Utils.isNotEmpty(payload.oldPassword) && Utils.isNotEmpty(payload.newPassword) && Utils.isNotEmpty(payload.confirmPassword)) {
            if (payload.newPassword === payload.confirmPassword) {
                const request: ChangePasswordRequest = {
                    OldPassword: payload.oldPassword,
                    NewPassword: payload.newPassword
                };
                const answer: [HttpReject, ChangePasswordResponse] = await Utils.to(this.sendToLoginSecure(request, "ChangePassword"));
                if (answer[0]) {
                    Platform.dispatch(SetAuthStatus({
                        authStatus: {changePasswordError: Translations.text(TranslationKey.errorGeneral)}
                    }));
                } else {
                    const response: ChangePasswordResponse = answer[1];
                    if (response.SuccessStatus) {
                        Platform.dispatch(SetAuthStatus({
                            authStatus: {changePasswordError: null}
                        }));
                        Platform.dispatch(SetTradingModal({
                            tradingModalType: TradingModalType.ChangePassword,
                            info: {
                                visible: false
                            }
                        }));
                        Platform.dispatch(AddInboxMessage({
                            message: {
                                type: InboxMessageType.General,
                                parameters: [
                                    Parameter.Of(InboxParameter.TrKey, TranslationKey.passwordChanged)
                                ]
                            }
                        }));
                    } else {
                        Platform.dispatch(SetAuthStatus({
                            authStatus: {changePasswordError: response.LocalizedErrorMessage}
                        }));
                    }
                }
            } else {
                Platform.dispatch(SetAuthStatus({
                    authStatus: {changePasswordError: Translations.text(TranslationKey.errorPasswordsNotMatch)}
                }));
            }
        } else {
            this._logger.warn("Change password details absent.");
            Platform.dispatch(SetAuthStatus({
                authStatus: {changePasswordError: Translations.text(TranslationKey.errorFieldsMandatory)}
            }));
        }
    }

    private sendToLogin = (request: any, path: string): Promise<any> => {
        return Xhr.sendTo(ServerType.Login, request, "loginService.svc/json/" + path);
    }

    private sendToLoginSecure = (request: any, path: string): Promise<any> => {
        return XhrUtil.sendToLogin(request, "loginsecureService.svc/json/" + path);
    }

    private pingAccountService = (): Promise<any> => {
        return XhrUtil.sendAuthenticated({}, "WebProfitServer/AccountStatesService.svc/json/GetAccountState");
    }
}
